import { useEffect, useRef } from "react";
import { Modbus as ModbusSdk, Site as SiteSdk } from "coolremote-sdk";
import { IModbusUnit } from "../screens/Modbus/types";
import useMutation from "./useMutation";
import useQuery from "./useQuery";
import { IModbusDevice } from "../screens/Modbus/types";
import { useStoreActions } from "../models/RootStore";

interface IMutationOptions {
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

const oid = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.$oid ?? v?._id?.$oid ?? undefined;

const oidStr = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.$oid ?? undefined;

/** BE stores `paramTemplateData.uiTemplate` as a JSON-encoded string, not an object. */
const parseUiTemplate = (raw: unknown): Record<string, any> | undefined => {
  if (!raw) {
    return undefined;
  }

  if (typeof raw !== "string") {
    return raw as Record<string, any>;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * Fetches all modbus units for a site and enriches each with its device's data.
 *
 * `line` lives only on the modbus DEVICE — unlike BACnet, the backend never
 * denormalizes it onto the unit (compare bacnetsController.createBacnetUnit),
 * and the site-management payload carries no modbus devices at all. So any
 * per-line grouping of modbus units has to be joined here on the client.
 */
export const getSiteModbusUnitsWithDevices = async (siteId: string): Promise<Record<string, any>> => {
  const [unitsRes, devicesRes] = await Promise.all([
    SiteSdk.getSiteModbusUnits(siteId),
    SiteSdk.getSiteModbusDevices(siteId),
  ]);

  const unitsDict = unitsRes?.data ?? unitsRes ?? {};
  const devicesDict = devicesRes?.data ?? devicesRes ?? {};

  const deviceById: Record<string, any> = {};
  Object.values(devicesDict).forEach((d: any) => {
    const id = oidStr(d._id) ?? d?.id;
    if (id) deviceById[id] = d;
  });

  const result: Record<string, any> = {};
  Object.values(unitsDict).forEach((u: any) => {
    const id = oidStr(u._id) ?? u?.id;
    if (!id) return;
    const deviceId = oidStr(u.modbusDevice) ?? (typeof u.modbusDevice === "string" ? u.modbusDevice : undefined);
    const device = deviceId ? deviceById[deviceId] : null;

    result[id] = {
      ...u,
      id,
      modbusDevice: deviceId,
      // CoolAutomation device the unit hangs off — used to scope units per device tile.
      device: oidStr(u.device) ?? u.device,
      // Joined from the modbus device; absent on the unit itself.
      line: device?.line,
      modbusDeviceName: device?.name,
      slaveId: device?.slaveId,
      isConnected: u.isConnected ?? device?.isConnected ?? false,
      // Whether the modbus DEVICE is bound (isEngaged lives on the device, not
      // the unit) — Remote Control hides a unit entirely until its device is
      // bound, distinct from the unit's own `isEngaged`/isVisible flags.
      deviceEngaged: !!device?.isEngaged,
      isVisible: u.isVisible !== false,
      rows: u.paramTemplateData?.rows || [],
      entityTemplate: u.paramTemplateData?.entityTemplate ?? false,
      entityType: u.paramTemplateData?.entityType,
      uiTemplate: parseUiTemplate(u.paramTemplateData?.uiTemplate),
    };
  });

  return result;
};

/**
 * Fetches a site's modbus units WITHOUT joining device data — skips the
 * getSiteModbusDevices call entirely. Use this instead of
 * getSiteModbusUnitsWithDevices when the caller doesn't need the device-joined
 * line/modbusDeviceName/isConnected-fallback (e.g. the Automation Rule /
 * Interlock condition-and-action picker).
 */
export const getSiteModbusUnitsOnly = async (siteId: string) => {
  const unitsRes = await SiteSdk.getSiteModbusUnits(siteId);
  return unitsRes?.data ?? unitsRes ?? {};
};

const MODBUS_WS_REFRESH_MESSAGE_NAMES = new Set([
  "MODBUS_UNIT_DISCONNECTED",
  "MODBUS_UNIT_RECONNECTED",
  "MODBUS_UNIT_ADDED",
  "MODBUS_DEVICE_DISCONNECTED",
  "MODBUS_DEVICE_RECONNECTED",
  "MODBUS_DEVICE_ADDED",
]);

/**
 * Refetches whenever the backend pushes a live modbus device/unit
 * connectivity or creation event for this site, so modbus screens reflect
 * gateway-driven changes without waiting on manual refetch/polling.
 * `refetch` doesn't need to be stable across renders — the latest one is
 * always used, and the listener itself is only re-registered when `siteId`
 * changes.
 */
export const useModbusConnectivityWs = (siteId: string | undefined, refetch: () => void) => {
  const { addWebSocketListener, removeWebSocketListener } = useStoreActions((a: any) => a);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!siteId) {
      return undefined;
    }

    const handle = (message: any) => {
      if (!MODBUS_WS_REFRESH_MESSAGE_NAMES.has(message?.name)) {
        return;
      }

      if (message.data?.siteId !== siteId) {
        return;
      }

      refetchRef.current();
    };
    addWebSocketListener(handle);
    return () => removeWebSocketListener(handle);
  }, [siteId, addWebSocketListener, removeWebSocketListener]);
};

export const useGetSiteModbusUnitsWithDevices = (siteId: string | undefined) => {
  return useQuery({
    queryKey: ["siteModbusUnitsWithDevices", siteId],
    queryFn: async (): Promise<Record<string, any>> =>
      getSiteModbusUnitsWithDevices(siteId as string),
    onSuccess: undefined,
    onError: undefined,
    enabled: !!siteId,
  });
};

export const normalizeModbusDevice = (doc: any): IModbusDevice => ({
  ...doc,
  id: oid(doc?._id) ?? doc?.id,
  site: oid(doc?.site),
  device: oid(doc?.device),
  deviceTemplate: oid(doc?.deviceTemplate) ?? doc?.deviceTemplate,
});

export const useGetSiteModbusDevices = (siteId: string | undefined) => {
  return useQuery({
    queryKey: ["siteModbusDevices", siteId],
    queryFn: async (): Promise<IModbusDevice[]> => {
      const res = await SiteSdk.getSiteModbusDevices(siteId);
      const dict = res?.data ?? res ?? {};
      return Object.values(dict).map(normalizeModbusDevice);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: !!siteId,
  });
};

export const useGetModbusDevice = (modbusDeviceId: string | undefined) => {
  return useQuery({
    queryKey: ["modbusDevice", modbusDeviceId],
    queryFn: async (): Promise<IModbusDevice> => {
      const res = await ModbusSdk.getModbusDevice(modbusDeviceId as string);
      return normalizeModbusDevice(res?.data ?? res);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: !!modbusDeviceId,
  });
};

export const useCreateModbusDevice = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await ModbusSdk.createModbusDevice(data);
      return normalizeModbusDevice(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useUpdateModbusDevice = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ modbusDeviceId, data }: { modbusDeviceId: string; data: Record<string, any> }) => {
      const res = await ModbusSdk.updateModbusDevice(modbusDeviceId, data);
      return normalizeModbusDevice(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useDeleteModbusDevice = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (modbusDeviceId: string) => {
      // DELETE resolves with no `.data`; xhr throws on non-2xx, so reaching here = success.
      await ModbusSdk.deleteModbusDevice(modbusDeviceId);
      return { success: true };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

/** Fetches the units belonging to a single Modbus device (normalized). */
export const getDeviceModbusUnits = async (
  deviceId: string,
  siteId: string | undefined,
): Promise<IModbusUnit[]> => {
  const res = await SiteSdk.getSiteModbusUnits(siteId);
  const dict = res?.data ?? res ?? {};
  const all: any[] = Object.values(dict);
  const oidStr = (v: any): string | undefined =>
    typeof v === "string" ? v : v?.$oid ?? undefined;
  return all
    .filter((u) => oidStr(u.modbusDevice) === deviceId)
    .map((u) => ({
      ...u,
      id: oidStr(u._id) ?? u.id,
      modbusDevice: oidStr(u.modbusDevice),
      functionalTemplate: oidStr(u.functionalTemplate) ?? u.functionalTemplate,
    }));
};

export const useGetDeviceModbusUnits = (deviceId: string | undefined, siteId: string | undefined) => {
  return useQuery({
    queryKey: ["deviceModbusUnits", deviceId, siteId],
    queryFn: async (): Promise<IModbusUnit[]> => getDeviceModbusUnits(deviceId as string, siteId),
    onSuccess: undefined,
    onError: undefined,
    enabled: !!deviceId && !!siteId,
  });
};

/* ---- Units ---- */

export const useCreateModbusUnit = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ modbusDeviceId, data }: { modbusDeviceId: string; data: Record<string, any> }) => {
      const res = await ModbusSdk.createModbusUnit(modbusDeviceId, data);
      return res?.data ?? res;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useUpdateModbusUnit = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ modbusUnitId, data }: { modbusUnitId: string; data: Record<string, any> }) => {
      const res = await ModbusSdk.updateModbusUnit(modbusUnitId, data);
      return res?.data ?? res;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useDeleteModbusUnit = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (modbusUnitId: string) => {
      await ModbusSdk.deleteModbusUnit(modbusUnitId);
      return { success: true };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useReadModbusRegister = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: {
      device: string;
      iface: string;
      slave: number;
      timeout?: number;
      registerType: number;
      registerAddress: number;
      dataType: string;
      multiplier?: number;
      bitNumber?: number | null;
      readFormula?: string;
      host?: string;
      port?: number;
    }) => {
      return ModbusSdk.readModbusRegister(data);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useValidateModbusFormula = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: { readFormula: string }) => {
      return ModbusSdk.validateModbusFormula(data);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useSetModbusUnitRegisterValue = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({
      modbusUnitId,
      data,
    }: {
      modbusUnitId: string;
      data: { register: any; value: any };
    }) => {
      const res = await ModbusSdk.setModbusUnitRegisterValue(modbusUnitId, data);
      return res?.data ?? res;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};
